// "Share by magic link" — the invite panel's second way in, under the email
// form (INVITE-MAGIC-LINK-1). Nika 2026-10-09: "also add share by magic link in
// invite dropdown. sharing magic link should go to them same invite flow as it
// works from email".
//
// A magic link is an ORG invitation bound to no email: the first person who
// opens it and accepts joins at the chosen role, once (the server claims it
// atomically), through the same accept page an emailed invitation lands on.
// Who may make one is exactly who may invite by email: `invitePolicy` here,
// the same route gate and role ceiling on the server. A link grants at most
// `maintainer` (the policy's `linkRoles`; the server answers 400
// `link_role_too_high` above it), and an org keeps at most 25 open links: the
// server answers 409 `too_many_open_links`, and the status line under the
// button says "Revoke an open link first." (the panel keeps no count of its
// own).
//
// THE SHAPE (only when `magicLinkOffered` — functions/inviteRoles.js — says
// so: an org context, a caller who may invite, a link role, and an SDK with
// `createOrgInvitationLink`):
//   CaptionTitle_link  "Share by magic link"
//   LinkActions   role select (AppSelect) · "Copy magic link"
//                 (AppCapsuleBtnToolbar — the secondary role; "Send invite"
//                 stays the panel's ONE primary). One click = ONE link, copied.
//   LinkStatusRow one status line (role="status"): the hint before, the
//                 result after, and a quiet "Revoke" for the link just made in
//                 a reserved box. Hidden sizers hold the hint and the result in
//                 the same grid cell, so the line keeps the height of its
//                 longest message and nothing under it moves (Rule 66).
//   ActiveLinks   the org's open links: role · ends {date} · Revoke.
//
// NO RE-COPY. The signed link is a bearer credential: the server returns it
// once (the create response), never stores it and never lists it. It goes
// straight to the clipboard and is never kept in state or logged. The list
// shows only a role and an end date.
//
// THE DATES ARE THE SERVER'S. "Ends {date}" is a row's `expiresAt`; the day
// count in the hint is the span the server gave the org's newest open
// invitation (expiresAt − createdAt). No client constant: with no open
// invitation yet, the hint says only that the link works once.
//
// FA204 — every helper the handlers read travels on `scope`, and each name
// carries the `magicLink` prefix: `el.scope` is one bag per app (the member
// list keeps its own `load` there).
export const MagicLinkInInvite = {
  scope: {
    magicLinkOrgId(s) {
      const root = (s && s.root) || {}
      return (root.jwtClaims && root.jwtClaims.workspace_org_id) || null
    },

    // The role the select shows: the chosen one while the policy offers it
    // for a link, else the policy default (`member`, which every caller who
    // may make a link may grant).
    magicLinkRole(el, s) {
      const p = el.call('invitePolicy', s)
      const roles = p.linkRoles
      if (roles.some((r) => r.value === s.role)) return s.role
      if (roles.some((r) => r.value === p.defaultRole)) return p.defaultRole
      return roles.length ? roles[0].value : null
    },

    // The translated name of a role.
    magicLinkRoleText(el, s, role) {
      const key = el.call('invitePolicy', s).labelKey(role)
      return key ? el.call('polyglot', key) : role || ''
    },

    // A server timestamp as a short date in the session language.
    magicLinkDate(el, value) {
      return value ? el.call('localizedDate', value, { month: 'short', day: 'numeric' }) : ''
    },

    // The hint line, for the role the select shows.
    magicLinkHint(el, s) {
      const sc = el.lookup('MagicLinkInInvite').scope
      const role = sc.magicLinkRoleText(el, s, sc.magicLinkRole(el, s))
      const days = Number(s.ttlDays) || 0
      const key =
        days === 1
          ? 'members.inviteDropdown.magicLink.hintOneDay'
          : days > 1
            ? 'members.inviteDropdown.magicLink.hintDays'
            : 'members.inviteDropdown.magicLink.hint'
      return el.call('polyglot', key).replace('{role}', role).replace('{days}', String(days))
    },

    // The result line for the link just made — or, before there is one, the
    // same sentence for the role the select shows (it sizes the line).
    magicLinkCreatedText(el, s) {
      const sc = el.lookup('MagicLinkInInvite').scope
      const role = sc.magicLinkRoleText(el, s, s.createdRole || sc.magicLinkRole(el, s))
      return el
        .call('polyglot', 'members.inviteDropdown.magicLink.created')
        .replace('{role}', role)
        .replace('{date}', sc.magicLinkDate(el, s.createdEndsAt))
    },

    // Plain copies of the open-link rows (state rows carry methods).
    magicLinkRows(s) {
      return (s.links || []).map((l) => ({
        key: l.key,
        inviteId: l.inviteId,
        role: l.role,
        endsAt: l.endsAt,
        busy: false
      }))
    },

    // The org's OPEN links: pending, unexpired, kind 'link'. The list route
    // returns every pending invitation of the org (email ones too) and never
    // a link URL. Its newest row also gives the hint its day count.
    async magicLinkLoad(host, s) {
      const orgId = host.scope.magicLinkOrgId(s)
      if (!orgId) return
      if (s.linksStatus !== 'loading') s.update({ linksStatus: 'loading' })
      try {
        const rows = (await host.sdk.listOrgInvitations(orgId)) || []
        const now = Date.now()
        const dated = rows.find((r) => r && r.createdAt && r.expiresAt)
        const span = dated ? Date.parse(dated.expiresAt) - Date.parse(dated.createdAt) : 0
        const links = rows
          .filter(
            (r) => r && r.kind === 'link' && r.status === 'pending' && Date.parse(r.expiresAt) > now
          )
          .map((r) => {
            const id = String(r._id || r.id || '')
            return {
              // A data key that can never read as an HTML tag name.
              key: 'magicLink_' + id,
              inviteId: id,
              role: r.role || '',
              endsAt: r.expiresAt,
              busy: false
            }
          })
        s.update({
          links,
          linksStatus: 'ready',
          ttlDays: span > 0 ? Math.round(span / 86400000) : 0
        })
      } catch (err) {
        console.warn('[MagicLinkInInvite] load failed:', err && err.message ? err.message : err)
        s.update({ links: [], linksStatus: 'error' })
      }
    },

    // ONE click = ONE link: create it at the select's role and copy it.
    // NOT async on purpose: the clipboard write must START inside the click
    // (copyTextWhenReady), before the first await — Safari ends the user
    // gesture there and would copy nothing.
    magicLinkCreate(host, s) {
      if (s.status === 'creating') return null
      if (!host.call('magicLinkOffered', s)) return null
      const role = host.scope.magicLinkRole(host, s)
      const orgId = host.scope.magicLinkOrgId(s)
      const failed = host.call('polyglot', 'members.inviteDropdown.magicLink.failed')
      s.update({
        status: 'creating',
        failure: '',
        createdId: '',
        createdRole: '',
        createdEndsAt: '',
        createdCopied: false,
        revokedCreated: false
      })
      const request = Promise.resolve().then(() =>
        host.sdk.createOrgInvitationLink(orgId, { role })
      )
      const copied = host.call(
        'copyTextWhenReady',
        request.then((link) => (link && link.inviteUrl) || '')
      )
      return Promise.all([request, copied])
        .then(([link, ok]) => {
          const id = String((link && (link._id || link.id)) || '')
          if (!id || !link.inviteUrl) throw new Error(failed)
          const endsAt = link.expiresAt || ''
          const span =
            link.createdAt && link.expiresAt
              ? Date.parse(link.expiresAt) - Date.parse(link.createdAt)
              : 0
          s.update({
            status: 'idle',
            createdId: id,
            createdRole: link.role || role,
            createdEndsAt: endsAt,
            createdCopied: ok === true,
            ttlDays: span > 0 ? Math.round(span / 86400000) : s.ttlDays,
            linksStatus: 'ready',
            links: [
              {
                key: 'magicLink_' + id,
                inviteId: id,
                role: link.role || role,
                endsAt,
                busy: false
              },
              ...host.scope.magicLinkRows(s).filter((l) => l.inviteId !== id)
            ]
          })
        })
        .catch((err) => {
          console.warn('[MagicLinkInInvite] create failed:', err && err.message ? err.message : err)
          // The org's open-link cap (409): say what to do, in the session
          // language — the server's code, never its English message.
          const code = err && err.cause && err.cause.error
          s.update({
            status: 'idle',
            failure:
              code === 'too_many_open_links'
                ? host.call('polyglot', 'members.inviteDropdown.magicLink.tooMany')
                : host.call('userFacingErrorMessage', err, {
                    networkMessage: host.call('polyglot', 'members.invite.offline'),
                    fallback: (e) => {
                      const msg = String((e && e.message) || '').trim()
                      return msg && msg !== failed ? failed + ': ' + msg : failed
                    }
                  })
          })
        })
    },

    // Revoke one open link — from its list row (`row` is that row's state)
    // or from the status line (the link just made).
    async magicLinkRevoke(host, s, inviteId, row) {
      if (!inviteId) return
      const fromLine = !row
      if (row && row.busy) return
      if (fromLine && s.revokingCreated) return
      const orgId = host.scope.magicLinkOrgId(s)
      const isCreated = s.createdId === inviteId
      const failed = host.call('polyglot', 'members.inviteDropdown.magicLink.revokeFailed')
      if (row) row.update({ busy: true })
      s.update({ failure: '', revokingCreated: fromLine })
      try {
        await host.sdk.revokeOrgInvitation(orgId, inviteId)
        const patch = {
          revokingCreated: false,
          links: host.scope.magicLinkRows(s).filter((l) => l.inviteId !== inviteId)
        }
        if (isCreated) {
          Object.assign(patch, {
            createdId: '',
            createdRole: '',
            createdEndsAt: '',
            revokedCreated: true
          })
        }
        s.update(patch)
      } catch (err) {
        console.warn('[MagicLinkInInvite] revoke failed:', err && err.message ? err.message : err)
        if (row) row.update({ busy: false })
        s.update({
          revokingCreated: false,
          failure: host.call('userFacingErrorMessage', err, {
            networkMessage: host.call('polyglot', 'members.invite.offline'),
            fallback: (e) => {
              const msg = String((e && e.message) || '').trim()
              return msg ? failed + ': ' + msg : failed
            }
          })
        })
      }
    }
  },

  // The section owns its model (a NON-EMPTY store that declares every key it
  // writes; writes are `update`, never `set`).
  //   role             the select ('' = the policy default)
  //   status           'idle' | 'creating'
  //   failure          a failed create / revoke, user-facing
  //   createdId/Role/EndsAt   the link just made (never its URL)
  //   createdCopied    the clipboard took it (false: say so — never claim a copy)
  //   revokingCreated  the status line's Revoke is busy
  //   revokedCreated   the link just made was revoked
  //   links            the org's open links: { key, inviteId, role, endsAt, busy }
  //   linksStatus      'idle' | 'loading' | 'ready' | 'error'
  //   ttlDays          the server's link lifetime, in days (0 = not known yet)
  state: {
    role: '',
    status: 'idle',
    failure: '',
    createdId: '',
    createdRole: '',
    createdEndsAt: '',
    createdCopied: false,
    revokingCreated: false,
    revokedCreated: false,
    links: [],
    linksStatus: 'idle',
    ttlDays: 0
  },

  flow: 'y',
  gap: 'Z',
  hide: (el, s) => !el.call('magicLinkOffered', s),
  onInit: (el, s) => {
    if (el.call('magicLinkOffered', s)) el.scope.magicLinkLoad(el, s)
  },

  CaptionTitle_link: {
    Text: { text: (el) => el.call('polyglot', 'members.inviteDropdown.magicLink.title') }
  },

  // One line where it fits; on a narrow panel the button wraps under the
  // select instead of the select clipping its label (same law as the email
  // form's row).
  LinkActions: {
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    gap: 'Z',
    AppSelect: {
      name: 'magicLinkRole',
      flex: '1 0 auto',
      width: 'auto',
      aria: { label: (el) => el.call('polyglot', 'members.inviteForm.roleOfMember') },
      value: (el, s) => el.lookup('MagicLinkInInvite').scope.magicLinkRole(el, s),
      onChange: (ev, el, s) => {
        s.update({ role: ev.target.value })
      },
      childrenAs: 'props',
      childProps: {
        tag: 'option',
        // `invitePolicy` hands over polyglot KEYS.
        text: (el) => el.call('polyglot', el.labelKey),
        attr: {
          selected: (el, s) =>
            el.value === el.lookup('MagicLinkInInvite').scope.magicLinkRole(el, s) ? '' : null
        }
      },
      // Only the roles a link may grant (at most maintainer).
      children: (el, s) => el.call('invitePolicy', s).linkRoles
    },

    // Secondary, label-stable while busy.
    AppCapsuleBtnToolbar: {
      flexShrink: '0',
      disabled: (el, s) => s.status === 'creating',
      ariaBusy: (el, s) => (s.status === 'creating' ? 'true' : null),
      AppLabelStack: {
        labels: [
          'members.inviteDropdown.magicLink.copy',
          'members.inviteDropdown.magicLink.creating'
        ],
        shown: (el, s) => (s.status === 'creating' ? 1 : 0)
      },
      onClick: (ev, el, s) => {
        ev.preventDefault()
        const host = el.lookup('MagicLinkInInvite')
        if (host) host.scope.magicLinkCreate(host, s)
      }
    }
  },

  LinkStatusRow: {
    flow: 'x',
    align: 'start',
    gap: 'Z',
    LinkStatus: {
      flex: '1',
      minWidth: '0',
      display: 'grid',
      // The ONE live line: the current message.
      LinkStatusText: {
        tag: 'p',
        role: 'status',
        gridArea: '1 / 1',
        margin: '0',
        fontSize: 'Y1',
        color: (el, s) => (s.failure ? 'dangerInk' : 'caption'),
        text: (el, s) => {
          const sc = el.lookup('MagicLinkInInvite').scope
          if (s.failure) return s.failure
          if (s.createdId) {
            return s.createdCopied
              ? sc.magicLinkCreatedText(el, s)
              : el.call('polyglot', 'members.inviteDropdown.magicLink.notCopied')
          }
          if (s.revokedCreated)
            return el.call('polyglot', 'members.inviteDropdown.magicLink.revoked')
          return sc.magicLinkHint(el, s)
        }
      },
      // Sizers: never seen, never announced — they only hold the line at the
      // height of its longest message.
      LinkHintSizer: {
        tag: 'span',
        gridArea: '1 / 1',
        fontSize: 'Y1',
        visibility: 'hidden',
        aria: { hidden: 'true' },
        text: (el, s) => el.lookup('MagicLinkInInvite').scope.magicLinkHint(el, s)
      },
      LinkResultSizer: {
        tag: 'span',
        gridArea: '1 / 1',
        fontSize: 'Y1',
        visibility: 'hidden',
        aria: { hidden: 'true' },
        text: (el, s) => el.lookup('MagicLinkInInvite').scope.magicLinkCreatedText(el, s)
      }
    },

    // The quiet "Revoke" for the link just made. Its box is always there;
    // hidden, it is out of the tab order and the accessibility tree.
    AppCapsuleBtnGhost: {
      compact: true,
      flexShrink: '0',
      visibility: (el, s) => (s.createdId ? 'visible' : 'hidden'),
      tabindex: (el, s) => (s.createdId ? null : '-1'),
      aria: { hidden: (el, s) => (s.createdId ? null : 'true') },
      disabled: (el, s) => !!s.revokingCreated,
      ariaBusy: (el, s) => (s.revokingCreated ? 'true' : null),
      AppLabelStack: {
        labels: [
          'members.inviteDropdown.magicLink.revoke',
          'members.inviteDropdown.magicLink.revoking'
        ],
        shown: (el, s) => (s.revokingCreated ? 1 : 0)
      },
      onClick: (ev, el, s) => {
        ev.preventDefault()
        const host = el.lookup('MagicLinkInInvite')
        if (host && s.createdId) host.scope.magicLinkRevoke(host, s, s.createdId, null)
      }
    }
  },

  ActiveLinks: {
    flow: 'y',
    gap: 'Y',
    hide: (el, s) => s.linksStatus !== 'error' && !(s.links || []).length,

    CaptionTitle_active: {
      Text: { text: (el) => el.call('polyglot', 'members.inviteDropdown.magicLink.active') }
    },

    LinkLoadError: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z',
      hide: (el, s) => s.linksStatus !== 'error',
      LinkLoadMessage: {
        tag: 'span',
        role: 'alert',
        fontSize: 'Z1',
        color: 'caption',
        text: (el, s) =>
          s.linksStatus === 'error'
            ? el.call('polyglot', 'members.inviteDropdown.magicLink.loadFailed')
            : ''
      },
      AppCapsuleBtnGhost: {
        compact: true,
        text: (el) => el.call('polyglot', 'members.invite.retry'),
        onClick: (ev, el) => {
          ev.preventDefault()
          const host = el.lookup('MagicLinkInInvite')
          if (host) host.scope.magicLinkLoad(host, host.state)
        }
      }
    },

    LinkList: {
      tag: 'ul',
      flow: 'y',
      margin: '0',
      padding: '0',
      listStyle: 'none',
      hide: (el, s) => s.linksStatus === 'error',
      children: (el, s) => s.links || [],
      childrenAs: 'state',
      childProps: {
        tag: 'li',
        flow: 'x',
        align: 'center',
        gap: 'Z',
        padding: 'Y 0',
        minWidth: '0',
        LinkWhen: {
          tag: 'span',
          flex: '1',
          minWidth: '0',
          fontSize: 'Z1',
          color: 'title',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) => {
            const sc = el.lookup('MagicLinkInInvite').scope
            return el
              .call('polyglot', 'members.inviteDropdown.magicLink.row')
              .replace('{role}', sc.magicLinkRoleText(el, s, s.role))
              .replace('{date}', sc.magicLinkDate(el, s.endsAt))
          }
        },
        AppCapsuleBtnGhost: {
          compact: true,
          flexShrink: '0',
          disabled: (el, s) => !!s.busy,
          ariaBusy: (el, s) => (s.busy ? 'true' : null),
          aria: {
            label: (el, s) => {
              const sc = el.lookup('MagicLinkInInvite').scope
              return el
                .call('polyglot', 'members.inviteDropdown.magicLink.revokeLabel')
                .replace('{role}', sc.magicLinkRoleText(el, s, s.role))
                .replace('{date}', sc.magicLinkDate(el, s.endsAt))
            }
          },
          AppLabelStack: {
            labels: [
              'members.inviteDropdown.magicLink.revoke',
              'members.inviteDropdown.magicLink.revoking'
            ],
            shown: (el, s) => (s.busy ? 1 : 0)
          },
          onClick: (ev, el, s) => {
            ev.preventDefault()
            const host = el.lookup('MagicLinkInInvite')
            if (host) host.scope.magicLinkRevoke(host, host.state, s.inviteId, s)
          }
        }
      }
    }
  }
}
